import { Component, Input, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, ElementRef, HostListener, AfterViewInit, OnDestroy, OnChanges, SimpleChanges, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { WidgetEdit } from '../../../../shared/components/widget-edit/widget-edit';

interface CardOffer {
    id: number;
    cardTitle: string;
    cardImage: string;
    apr: string;
    loanAmount: string;
    loanTerm: string;
}

@Component({
    selector: 'app-card-offers-widget',
    standalone: true,
    imports: [CommonModule, WidgetEdit, CdkOverlayOrigin],
    templateUrl: './card-offers.html',
    styleUrls: ['./card-offers.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardOffersWidget implements AfterViewInit, OnDestroy, OnChanges {
    @Input() title = 'Card Offers';
    @Input() size: 'small' | 'medium' | 'half' | 'full' = 'medium';
    @Input() index = 0;
    @Input() isEditMode = false;
    @Input() isAddMode: boolean = false;
    @Output() addWidget = new EventEmitter<void>();
    @Input() activeDropdownIndex: number | null = null;
    @Input() getAvailableSizes!: (index: number) => Array<{ value: string; label: string }>;
    @Input() toggleSizeDropdown!: (index: number, event: Event) => void;
    @Input() changeWidgetSize!: (index: number, size: any) => void;
    @Input() removeWidget!: (index: number) => void;

    @ViewChild('sliderContainer') sliderContainer!: ElementRef;
    currentCardIndex = 0;
    sliderWidth = 0;
    cardWidth = 280; // Start with default, will be updated on resize
    cardGap = 12;    // Default gap, will be measured from DOM
    private resizeObserver: ResizeObserver | null = null;
    isDragging = false;

    @Input() cardOffers: CardOffer[] = [
        {
            id: 1,
            cardTitle: 'Best Points Card',
            cardImage: 'assets/images/card-american.jpg',
            apr: '12.66% APR',
            loanAmount: '$15,000',
            loanTerm: '36 Months'
        },
        {
            id: 2,
            cardTitle: 'Cash Back Rewards',
            cardImage: 'assets/images/fairstone-financial-inc-upsizes-credit.jpg',
            apr: '13.50% APR',
            loanAmount: '$20,000',
            loanTerm: '48 Months'
        },
        {
            id: 3,
            cardTitle: 'Travel Rewards',
            cardImage: 'assets/images/card-american.jpg',
            apr: '11.99% APR',
            loanAmount: '$25,000',
            loanTerm: '60 Months'
        },
        {
            id: 4,
            cardTitle: 'Travel Rewards',
            cardImage: 'assets/images/card-american.jpg',
            apr: '11.99% APR',
            loanAmount: '$25,000',
            loanTerm: '60 Months'
        },
        {
            id: 5,
            cardTitle: 'Travel Rewards',
            cardImage: 'assets/images/card-american.jpg',
            apr: '11.99% APR',
            loanAmount: '$25,000',
            loanTerm: '65 Months'
        },
        {
            id: 6,
            cardTitle: 'Travel Rewards',
            cardImage: 'assets/images/card-american.jpg',
            apr: '11.99% APR',
            loanAmount: '$235,000',
            loanTerm: '65 Months'
        }
    ];

    constructor(
        private cdr: ChangeDetectorRef,
        private elementRef: ElementRef
    ) { }

    private updateDetailItemPadding() {
        // Get the parent article.widget element
        const containerElement = this.elementRef.nativeElement as HTMLElement;
        if (!containerElement) return;

        const widgetElement = containerElement.closest('article.widget') as HTMLElement;
        if (!widgetElement) return;

        // Get all detail-item elements
        const detailItems = containerElement.querySelectorAll('.card-offers-widget__detail-item') as NodeListOf<HTMLElement>;
        if (!detailItems.length) return;

        const widgetHeight = widgetElement.offsetHeight;

        // Define min/max heights and corresponding padding values
        const minHeight = 296; // From RESIZE_CONFIGS
        const maxHeight = 362; // From RESIZE_CONFIGS
        const minPaddingVertical = 6;   // Minimum vertical padding
        const maxPaddingVertical = 12;  // Maximum vertical padding (original)
        const minPaddingHorizontal = 8;  // Minimum horizontal padding
        const maxPaddingHorizontal = 16; // Maximum horizontal padding (original)

        if (widgetHeight <= minHeight) {
            // At minimum height: remove inline style to use CSS default
            detailItems.forEach(item => {
                item.style.removeProperty('padding');
            });
        } else {
            // Above minimum height: calculate and apply dynamic padding
            const heightRatio = Math.min((widgetHeight - minHeight) / (maxHeight - minHeight), 1);
            const dynamicPaddingV = minPaddingVertical + (maxPaddingVertical - minPaddingVertical) * heightRatio;
            const dynamicPaddingH = minPaddingHorizontal + (maxPaddingHorizontal - minPaddingHorizontal) * heightRatio;

            detailItems.forEach(item => {
                item.style.padding = `${dynamicPaddingV}px ${dynamicPaddingH}px`;
            });
        }
    }


    ngOnChanges(changes: SimpleChanges) {
        if (changes['size']) {
            setTimeout(() => {
                this.updateSliderWidth();
            }, 100);
        }
    }

    ngAfterViewInit() {
        this.updateSliderWidth();

        // Find the parent article.widget element (same approach as expense-total-widget)
        const containerElement = this.sliderContainer?.nativeElement?.closest('article.widget') as HTMLElement;

        if (containerElement) {
            this.resizeObserver = new ResizeObserver(() => {
                window.requestAnimationFrame(() => {
                    if (!this.resizeObserver) return;
                    const isDragging = document.body.classList.contains('is-resizing');

                    if (isDragging) {

                        this.updateSliderWidth();
                        this.updateDetailItemPadding(); // Add dynamic padding adjustment
                        // Trigger change detection to update transform immediately
                        this.cdr.detectChanges();
                    }
                });
            });

            this.resizeObserver.observe(containerElement);

        }
    }

    ngOnDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    getSliderTransform(): string {
        if (this.sliderWidth === 0) {
            return 'translateX(0)';
        }
        const cardTotalWidth = this.cardWidth + this.cardGap;
        const padding = 0;

        // Calculate the offset to center the current card in the slider
        const centerOffset = (this.sliderWidth / 2) - (this.cardWidth / 2) - padding;
        const translateX = centerOffset - (this.currentCardIndex * cardTotalWidth);

        return `translateX(${translateX}px)`;
    }

    @HostListener('window:resize')
    onResize() {
        this.updateSliderWidth();
    }

    updateSliderWidth() {
        this.isDragging = document.body.classList.contains('is-resizing');

        if (this.sliderContainer) {
            this.sliderWidth = this.sliderContainer.nativeElement.offsetWidth;

            // Calculate dynamic card width based on available space
            // For larger widgets, allow cards to grow
            const minCardWidth = 280;
            const maxCardWidth = 380;

            // Calculate based on slider width - allow card to use more space when widget is larger
            if (this.size === 'half' || this.size === 'full') {
                // For larger sizes, make cards bigger
                const calculatedWidth = Math.min(maxCardWidth, Math.max(minCardWidth, this.sliderWidth * 0.85));
                this.cardWidth = Math.floor(calculatedWidth);
            } else if (this.size === 'medium') {
                // Medium size gets moderate increase
                const calculatedWidth = Math.min(320, Math.max(minCardWidth, this.sliderWidth * 0.75));
                this.cardWidth = Math.floor(calculatedWidth);
            } else {
                // Small size stays at minimum
                this.cardWidth = minCardWidth;
            }

            // Measure the actual gap from the DOM for stability
            const cardElement = this.sliderContainer.nativeElement.querySelector('.card-offers-widget__offer-card');
            if (cardElement) {
                const style = window.getComputedStyle(cardElement);
                this.cardGap = parseFloat(style.marginRight) || 12;
            }

            // Force immediate change detection to update transform
            setTimeout(() => {
                this.cdr.detectChanges();
            }, 0);
        }
    }

    previousCard(): void {
        this.isDragging = false;
        if (this.currentCardIndex > 0) {
            this.currentCardIndex--;
            this.cdr.markForCheck();
        }
    }

    nextCard(): void {
        this.isDragging = false;
        if (this.currentCardIndex < this.cardOffers.length - 1) {
            this.currentCardIndex++;
            this.cdr.markForCheck();
        }
    }

    canGoPrevious(): boolean {
        return this.currentCardIndex > 0;
    }

    canGoNext(): boolean {
        return this.currentCardIndex < this.cardOffers.length - 1;
    }

    onSelectCard(card: CardOffer): void {

    }
}
